import {
  PostType,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  IMAGE_MAX_BYTES,
  VIDEO_MAX_BYTES,
  VIDEO_MIN_DURATION,
  VIDEO_MAX_DURATION,
  IMAGE_MIN_WIDTH,
  IMAGE_MAX_WIDTH,
  VIDEO_MIN_WIDTH,
  VIDEO_MIN_HEIGHT,
  VIDEO_MAX_WIDTH,
  IMAGE_ASPECT_RATIO_MIN,
  IMAGE_ASPECT_RATIO_MAX,
  VIDEO_ASPECT_RATIO_MIN,
  VIDEO_ASPECT_RATIO_MAX,
} from "../types/scheduled-post"

export type MediaError = string

export type MediaValidationResult = {
  errors: MediaError[]
  warnings: MediaError[]
}

const ASPECT_RATIO_TOLERANCE = 0.02

export async function getMediaDimensions(
  src: File | string,
  isVideo: boolean
): Promise<{ width: number; height: number } | null> {
  const url = src instanceof File ? URL.createObjectURL(src) : src
  const shouldRevoke = src instanceof File

  return new Promise((resolve) => {
    if (isVideo) {
      const el = document.createElement("video")
      el.onloadedmetadata = () => {
        if (shouldRevoke) URL.revokeObjectURL(url)
        resolve({ width: el.videoWidth, height: el.videoHeight })
      }
      el.onerror = () => {
        if (shouldRevoke) URL.revokeObjectURL(url)
        resolve(null)
      }
      el.src = url
    } else {
      const el = new Image()
      el.onload = () => {
        if (shouldRevoke) URL.revokeObjectURL(url)
        resolve({ width: el.naturalWidth, height: el.naturalHeight })
      }
      el.onerror = () => {
        if (shouldRevoke) URL.revokeObjectURL(url)
        resolve(null)
      }
      el.src = url
    }
  })
}

export async function validateMediaFile(
  file: File,
  postType: PostType,
  index: number,
  referenceRatio?: number
): Promise<MediaValidationResult> {
  const errors: MediaError[] = []
  const warnings: MediaError[] = []
  const label = index > 0 ? `${index + 1}枚目の` : ""
  const isImage = file.type.startsWith("image/")
  const isVideo = file.type.startsWith("video/")

  // 1. 投稿タイプ別メディア種別制限
  if (postType === "feed" && isVideo) {
    errors.push(`${label}フィード投稿には動画をアップロードできません`)
    return { errors, warnings }
  }
  if (postType === "reel" && isImage) {
    errors.push(`${label}リール投稿には画像をアップロードできません`)
    return { errors, warnings }
  }

  // 2. MIMEタイプ
  if (isImage && !ALLOWED_IMAGE_TYPES[postType].includes(file.type)) {
    errors.push(`${label}画像はJPEGまたはPNGにしてください`)
    return { errors, warnings }
  }
  if (isVideo && !ALLOWED_VIDEO_TYPES.includes(file.type)) {
    errors.push(`${label}動画はMP4またはMOV形式にしてください`)
    return { errors, warnings }
  }
  if (!isImage && !isVideo) {
    errors.push(`${label}ファイルは画像または動画にしてください`)
    return { errors, warnings }
  }

  // 3. ファイルサイズ
  if (isImage && file.size > IMAGE_MAX_BYTES) {
    errors.push(`${label}画像は8MB以下にしてください`)
  }
  if (isVideo && file.size > VIDEO_MAX_BYTES[postType]) {
    errors.push(`${label}動画は${VIDEO_MAX_BYTES[postType] / (1024 * 1024)}MB以下にしてください`)
  }

  // 4. 動画の再生時間
  if (isVideo) {
    const duration = await getVideoDuration(file)
    const minDur = VIDEO_MIN_DURATION[postType]
    const maxDur = VIDEO_MAX_DURATION[postType]
    if (duration === 0) {
      errors.push(`${label}動画の再生時間を取得できませんでした`)
    } else if (duration < minDur || duration > maxDur) {
      errors.push(`${label}動画の長さは${minDur}〜${maxDur}秒にしてください（現在: ${duration.toFixed(1)}秒）`)
    }
  }

  // 5〜8. 解像度・アスペクト比チェック（dims を一度だけ取得）
  const dims = await getMediaDimensions(file, isVideo)

  // 5. カルーセルのアスペクト比（2枚目以降、相対チェック）
  if (postType === "carousel" && referenceRatio !== undefined && dims && dims.height > 0) {
    const ratio = dims.width / dims.height
    if (Math.abs(ratio - referenceRatio) > ASPECT_RATIO_TOLERANCE) {
      errors.push(`${label}アスペクト比が1枚目と異なります（1枚目: ${referenceRatio.toFixed(2)}、このファイル: ${ratio.toFixed(2)}）`)
    }
  }

  // 6. 画像解像度（幅 320〜1440px）
  if (isImage && dims) {
    if (dims.width < IMAGE_MIN_WIDTH) {
      errors.push(`${label}画像の幅は${IMAGE_MIN_WIDTH}px以上にしてください（現在: ${dims.width}px）`)
    } else if (dims.width > IMAGE_MAX_WIDTH) {
      errors.push(`${label}画像の幅は${IMAGE_MAX_WIDTH}px以下にしてください（現在: ${dims.width}px）`)
    }
  }

  // 7. 動画解像度（540×960px 以上、幅 1920px 以下）
  if (isVideo && dims) {
    if (dims.width < VIDEO_MIN_WIDTH || dims.height < VIDEO_MIN_HEIGHT) {
      errors.push(`${label}動画の解像度は${VIDEO_MIN_WIDTH}×${VIDEO_MIN_HEIGHT}px以上にしてください（現在: ${dims.width}×${dims.height}px）`)
    } else if (dims.width > VIDEO_MAX_WIDTH) {
      errors.push(`${label}動画の幅は${VIDEO_MAX_WIDTH}px以下にしてください（現在: ${dims.width}px）`)
    }
  }

  // 8. アスペクト比（絶対値チェック）
  if (dims && dims.height > 0) {
    const ratio = dims.width / dims.height
    if (isImage) {
      const aspectMin = IMAGE_ASPECT_RATIO_MIN[postType]
      const aspectMax = IMAGE_ASPECT_RATIO_MAX[postType]
      if (aspectMin !== null && aspectMax !== null) {
        if (ratio < aspectMin - ASPECT_RATIO_TOLERANCE || ratio > aspectMax + ASPECT_RATIO_TOLERANCE) {
          errors.push(`${label}画像のアスペクト比が対応範囲外です（${aspectMin.toFixed(2)}〜${aspectMax.toFixed(2)}、現在: ${ratio.toFixed(2)}）`)
        }
      } else if (postType === "story") {
        // Story 画像は 9:16 推奨（APIは強制しないため警告のみ）
        const recommended = 9 / 16
        if (Math.abs(ratio - recommended) > 0.05) {
          warnings.push(`${label}ストーリー画像は9:16（縦長）が推奨されます（現在: ${ratio.toFixed(2)}）`)
        }
      }
    }
    if (isVideo) {
      const aspectMin = VIDEO_ASPECT_RATIO_MIN[postType]
      const aspectMax = VIDEO_ASPECT_RATIO_MAX[postType]
      if (aspectMin !== null && aspectMax !== null) {
        if (ratio < aspectMin - ASPECT_RATIO_TOLERANCE || ratio > aspectMax + ASPECT_RATIO_TOLERANCE) {
          errors.push(`${label}動画のアスペクト比が対応範囲外です（${aspectMin.toFixed(2)}〜${aspectMax.toFixed(2)}、現在: ${ratio.toFixed(2)}）`)
        }
      }
    }
  }

  return { errors, warnings }
}

export function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement("video")
    const url = URL.createObjectURL(file)
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve(video.duration)
    }
    video.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(0)
    }
    video.src = url
  })
}
